import { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('pwa_install_dismissed') === '1');

  useEffect(() => {
    if (dismissed) return;

    // Check if already installed as PWA
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    if (isStandalone) return;

    // iOS detection
    const ios = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());
    const safari = /safari/.test(navigator.userAgent.toLowerCase()) && !/chrome/.test(navigator.userAgent.toLowerCase());
    if (ios && safari) {
      setIsIOS(true);
      // Show iOS prompt after 10 seconds
      const t = setTimeout(() => setShowBanner(true), 10000);
      return () => clearTimeout(t);
    }

    // Android/Chrome: listen for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [dismissed]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') dismiss();
      setDeferredPrompt(null);
    }
  };

  const dismiss = () => {
    setShowBanner(false);
    setDismissed(true);
    localStorage.setItem('pwa_install_dismissed', '1');
  };

  if (!showBanner || dismissed) return null;

  return (
    <div className="fixed bottom-20 md:bottom-4 left-1/2 -translate-x-1/2 z-[10002] w-full max-w-sm px-4">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
        {/* Brand strip */}
        <div className="bg-gradient-to-r from-[#F4C430] via-[#FF8C42] to-[#FF6B35] px-4 py-2 flex items-center gap-3">
          <img loading="lazy" src="/icons/foodcard-color.png" alt="FoodQuest" className="w-11 h-11 rounded-xl" />
          <div className="flex-1">
            <p className="text-white font-bold text-sm">Add FoodQuest to Home Screen</p>
            <p className="text-white/75 text-xs">Get the full app experience</p>
          </div>
          <button onClick={dismiss} className="text-white/70 hover:text-white p-1">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4">
          {isIOS ? (
            <div className="space-y-2">
              <p className="text-sm text-gray-700 font-medium">Install on iPhone / iPad:</p>
              <ol className="space-y-1.5">
                {[
                  ['1', 'Tap the Share button', '⬆️'],
                  ['2', 'Scroll down and tap', '"Add to Home Screen"'],
                  ['3', 'Tap', '"Add"'],
                ].map(([n, a, b]) => (
                  <li key={n} className="flex items-center gap-2 text-xs text-gray-600">
                    <span className="w-5 h-5 bg-[#FF6B35] text-white rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0">{n}</span>
                    {a} <span className="font-medium text-gray-900">{b}</span>
                  </li>
                ))}
              </ol>
              <button onClick={dismiss} className="w-full mt-2 py-2 text-sm text-gray-500 hover:text-gray-700">Got it, thanks!</button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <p className="text-sm text-gray-700">Works offline · Fast · No app store needed</p>
              </div>
              <button onClick={handleInstall}
                className="flex items-center gap-1.5 bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white px-4 py-2 rounded-2xl text-sm font-semibold hover:opacity-90 flex-shrink-0">
                <Download className="w-3.5 h-3.5" />Install
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
